import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, fromEvent } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { TranslateModule } from '@ngx-translate/core';
import { CrownAiBannerVisibilityService } from '@shared/services/crown-ai-banner-visibility.service';
import { RxIntuitiveTeethOrchestratorFacade } from '@modules/rx-intuitive-teeth-orchestrator/rx-intuitive-teeth-orchestrator.facade';
import { RxIntuitiveTeethDiagramFooterComponent } from '@modules/rx-intuitive-teeth-orchestrator/components/rx-intuitive-teeth-diagram-footer/rx-intuitive-teeth-diagram-footer.component';
import { RxIntuitiveTeethOrchestratorLayoutComponent } from '@modules/rx-intuitive-teeth-orchestrator/containers/rx-intuitive-teeth-orchestrator-layout/rx-intuitive-teeth-orchestrator-layout.component';
import { RxIntuitiveArtTreatmentViewlistComponent } from '../../components/rx-intuitive-art-treatment-viewlist/rx-intuitive-art-treatment-viewlist.component';
import { RxIntuitiveCopyFromBehaviorService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-copy-from-behavior.service';
import { ArtTreatmentIntegrity, ArtTreatmentDefinition } from '../../models';
import { IntuitiveIndication } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { RxIntuitiveSpriteService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-sprite-service';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { ArtTreatmentFacade } from '../../services/art-treatment.facade';
import { ArtTreatmentStore } from '../../state/art-treatment.store';
import { ArtTreatmentQuery } from '../../state/art-treatment.query';
import { RxIntuitiveArtTeethDiagramComponent } from '../../components/rx-intuitive-art-teeth-diagram/rx-intuitive-art-teeth-diagram.component';
import { RxIntuitiveArtTreatmentButtonsComponent } from '../../components/treatment-buttons/rx-intuitive-art-treatment-buttons/rx-intuitive-art-treatment-buttons.component';
import { isInsideEditingArea } from '@shared/utils/is-inside-editing-area.util';
import { ART_EDITING_AREA_SELECTORS } from '../../constants/art-editing-area-selectors';

@Component({
	selector: 'rx-intuitive-art-orchestrator',
	templateUrl: './rx-intuitive-art-orchestrator.component.html',
	styleUrls: ['./rx-intuitive-art-orchestrator.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		RxIntuitiveArtTeethDiagramComponent,
		RxIntuitiveArtTreatmentButtonsComponent,
		RxIntuitiveArtTreatmentViewlistComponent,
		RxIntuitiveTeethDiagramFooterComponent,
		RxIntuitiveTeethOrchestratorLayoutComponent
	],
	providers: [ArtTreatmentStore, ArtTreatmentQuery, ArtTreatmentFacade]
})
export class RxIntuitiveArtOrchestratorComponent extends BaseDestroyableComponent implements OnInit {
	@ViewChild(RxIntuitiveArtTeethDiagramComponent, { read: ElementRef }) private readonly teethDiagram?: ElementRef<HTMLElement>;

	readonly copyFromSourceMenuItems$ = this.copyFromBehaviorService.copyFromSourceMenuItems$;
	readonly isCopyFromToothControlDisabled$ = this.copyFromBehaviorService.isCopyFromMenuEnabled$.pipe(map(enabled => !enabled));
	readonly copyFromMenuDefaultIndicationId: number;

	readonly upperJaw$ = this.artFacade.upperJaw$;
	readonly lowerJaw$ = this.artFacade.lowerJaw$;

	readonly selectedToothIds$ = this.artFacade.selectedToothIds$;
	readonly hasSelectedTeeth$ = this.artFacade.hasSelectedTeeth$;

	readonly availableIntegrityId = this.artFacade.availableIntegrity$;
	readonly enabledIntegrity = this.artFacade.enabledIntegrity$;
	readonly selectedIntegrity$ = this.artFacade.selectedIntegrity$;

	readonly availableDefinitionIds = this.artFacade.availableDefinitions$;
	readonly enabledDefinitions$ = this.artFacade.enabledDefinitions$;
	readonly selectedDefinitions$ = this.artFacade.selectedDefinitions$;

	readonly treatmentListItems$ = this.artFacade.treatmentListItems$;

	private readonly pointerUp$ = fromEvent<PointerEvent>(document, 'pointerup');

	readonly isTeethDiagramReadOnly$ = this.artFacade.isTeethDiagramReadOnly$;

	readonly hasSidePanelContent$ = this.treatmentListItems$.pipe(map(items => items.length > 0));

	readonly showCopyFromMenu$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => !readOnly));

	readonly chartReadOnly$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => readOnly));

	readonly indicationButtonsDisabled$ = combineLatest([this.hasSelectedTeeth$, this.isTeethDiagramReadOnly$]).pipe(
		map(([hasSelected, readOnly]) => hasSelected !== true || !!readOnly)
	);

	readonly indicationButtonsAsLabel$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => readOnly));

	readonly showPanelBack$ = this.hasSidePanelContent$;

	readonly showSidePanelPlaceholder$ = combineLatest([this.treatmentListItems$, this.hasSelectedTeeth$]).pipe(
		map(([items, hasSelectedTeeth]) => items.length === 0 && !hasSelectedTeeth)
	);

	readonly isCrownAIBannerVisible$ = this.crownAiBannerVisibilityService.isVisible$;

	constructor(
		protected readonly facade: RxIntuitiveTeethOrchestratorFacade,
		protected readonly copyFromBehaviorService: RxIntuitiveCopyFromBehaviorService,
		protected readonly crownAiBannerVisibilityService: CrownAiBannerVisibilityService,
		protected readonly indicationMapper: RxIntuitiveIndicationMapperService,
		protected readonly artFacade: ArtTreatmentFacade,
		private readonly spriteService: RxIntuitiveSpriteService
	) {
		super();
		this.copyFromMenuDefaultIndicationId = this.indicationMapper.getDefaultIndication();
	}

	ngOnInit(): void {
		this.artFacade.initialize();

		combineLatest([this.upperJaw$, this.lowerJaw$])
			.pipe(takeUntil(this.componentAlive$))
			.subscribe(([upperJaw, lowerJaw]) => {
				const hasMissingIntegrity = [...upperJaw, ...lowerJaw].some(tooth =>
					[ArtTreatmentIntegrity.Missing, ArtTreatmentIntegrity.MissingRestorationPlanned].includes(tooth.artTreatment.integrity)
				);

				this.spriteService.syncInjectedSpritesContent(
					upperJaw,
					lowerJaw,
					hasMissingIntegrity ? [IntuitiveIndication.Missing_edentulousSpace] : []
				);
			});

		this.pointerUp$
			.pipe(
				takeUntil(this.componentAlive$),
				withLatestFrom(this.artFacade.hasSelectedTeeth$),
				filter(([event, hasSelectedTeeth]) => hasSelectedTeeth && !isInsideEditingArea(event.target, ART_EDITING_AREA_SELECTORS))
			)
			.subscribe(() => this.artFacade.clearToothSelection());
	}

	onCopyFromToothSourceSelected(toothId: number): void {
		this.facade.copyTreatmentDetailsFromSourceTooth(toothId);
	}

	onToothClicked(toothId: number): void {
		this.artFacade.toggleToothSelection(toothId);
	}

	onResetSelectedRequested(): void {
		this.artFacade.clearToothSelection();
	}

	onIntegritySelected(integrity: ArtTreatmentIntegrity): void {
		this.artFacade.assignIntegrityToSelectedTeeth(integrity);
	}

	onDefinitionSelected(definition: ArtTreatmentDefinition): void {
		this.artFacade.assignDefinitionToSelectedTeeth(definition);
	}

	onResetRequested(toothId: number): void {
		this.artFacade.resetTreatment(toothId);
	}

	focusDiagram(): void {
		this.teethDiagram?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
		this.teethDiagram?.nativeElement?.focus({ preventScroll: true });
	}
}
